import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress,
  Typography, Table, TableHead, TableRow, TableCell, TableBody, Alert, Box, Chip,
} from '@mui/material';
import { InvTextField } from '../InvField';
import { acceptVendorQuote } from '../../api/rfq';
import { createPoFromAcceptedQuote } from '../../api/procurement';
import { toAcceptQuoteBody, toPoFromQuoteBody, vendorDisplayLabel } from '../../utils/rfqEntity';
import {
  rankQuotes, perItemLowest, splitAwardSummary, approvalRoute,
  defaultSplitQtyAssignment, splitAwardGroupsFromQty, remainingQtyOnItem,
  requestedQty, quotedQtyForLine, parseAwardQty, awardedLineValue, splitQtyIssues,
} from '../../utils/rfqComparison';
import { formatCurrency, formatQty, generateTxnNumber } from '../../utils';
import { IPD_COLORS, cardSx } from '../../theme';

export default function RfqSplitAwardDialog({
  open,
  comparison,
  vendorDirectory = [],
  resolveItem,
  onClose,
  onSuccess,
}) {
  const ranked = useMemo(() => rankQuotes(comparison), [comparison]);
  const lowestByItem = useMemo(() => perItemLowest(comparison), [comparison]);
  const split = useMemo(() => splitAwardSummary(comparison, ranked), [comparison, ranked]);
  const items = useMemo(() => comparison?.items || [], [comparison]);

  const [assignment, setAssignment] = useState(() => defaultSplitQtyAssignment(comparison));
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAssignment(defaultSplitQtyAssignment(comparison));
    setRemarks('');
    setError(null);
    setProgress(null);
  }, [open, comparison]);

  const vendorMap = useMemo(() => {
    const map = {};
    vendorDirectory.forEach((v) => {
      const id = v.vendor_id || v.id;
      if (id) map[id] = vendorDisplayLabel(v);
    });
    return map;
  }, [vendorDirectory]);

  const quoteById = useMemo(() => {
    const map = {};
    ranked.forEach((q) => { map[q.quoteId] = q; });
    return map;
  }, [ranked]);

  const groups = useMemo(
    () => splitAwardGroupsFromQty(comparison, assignment, ranked),
    [comparison, assignment, ranked],
  );

  const issues = useMemo(() => splitQtyIssues(comparison, assignment), [comparison, assignment]);
  const itemLabel = (row) => (resolveItem ? resolveItem({ item_id: row.item_id }) : row.item_id);
  const leftover = items.filter((row) => remainingQtyOnItem(row, assignment[row.rfq_item_id]) > 0);
  const totalValue = groups.reduce((sum, g) => sum + g.value, 0);

  const blockers = [];
  if (issues.awardedCount === 0) blockers.push('Assign quantity to at least one vendor who quoted.');
  issues.overRequested.forEach((row) => {
    blockers.push(`${itemLabel(row)}: allocated qty is more than requested.`);
  });
  issues.overQuoted.forEach((row) => {
    blockers.push(`${itemLabel(row)}: a vendor is being given more than they quoted.`);
  });

  function setQty(rfqItemId, quoteId, value) {
    setAssignment((prev) => ({
      ...prev,
      [rfqItemId]: { ...(prev[rfqItemId] || {}), [quoteId]: value },
    }));
  }

  function fillRemaining(row, quoteId, line) {
    const cap = quotedQtyForLine(row, line);
    setAssignment((prev) => {
      const current = { ...(prev[row.rfq_item_id] || {}) };
      const others = remainingQtyOnItem(row, { ...current, [quoteId]: 0 });
      // remainingQtyOnItem with this vendor zeroed is the leftover others left.
      current[quoteId] = Math.min(cap, others);
      return { ...prev, [row.rfq_item_id]: current };
    });
  }

  async function handleSubmit() {
    setLoading(true);
    setError(null);
    const batch = generateTxnNumber('ACC');
    try {
      for (let i = 0; i < groups.length; i += 1) {
        const g = groups[i];
        setProgress(`Awarding ${i + 1} of ${groups.length}…`);
        const accepted = await acceptVendorQuote(g.quoteId, toAcceptQuoteBody({
          remarks,
          reject_others: true,
          awarded_rfq_item_ids: g.lines.map((l) => l.rfq_item_id),
          line_awards: g.lines.map((l) => ({
            rfq_item_id: l.rfq_item_id,
            awarded_quantity: l.awarded_quantity,
          })),
          acceptance_number: `${batch}-${i + 1}`,
        }));
        const acceptedQuoteId = accepted?.accepted_quote_id || accepted?.id;
        if (acceptedQuoteId) {
          try {
            await createPoFromAcceptedQuote(toPoFromQuoteBody(acceptedQuoteId, { remarks }));
          } catch (poErr) {
            // Award already placed; PO can be created from the Awarded section later.
          }
        }
      }
      onSuccess?.({ vendors: groups.length, value: totalValue });
      onClose?.();
    } catch (e) {
      setError(
        `${e.message || 'Split award failed'}. Awards already placed have been kept — `
        + 'reopen this dialog to award the remaining quantity.',
      );
    } finally {
      setLoading(false);
      setProgress(null);
    }
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { ...cardSx, p: 0.5 } }}>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>
        Split award — item and quantity
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!split.hasCompleteQuote && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No vendor priced every item, so there is no complete quote to compare this
            split against.
          </Alert>
        )}

        {split.hasCompleteQuote && split.saving > 0 && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Awarding item by item saves {formatCurrency(split.saving)} ({split.savingPct.toFixed(1)}%)
            against giving the whole RFQ to L1.
          </Alert>
        )}

        {leftover.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Remaining qty will not be awarded on:{' '}
            {leftover.map((r) => `${itemLabel(r)} (${formatQty(remainingQtyOnItem(r, assignment[r.rfq_item_id]))})`).join(', ')}.
            The RFQ stays open until that quantity is placed.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((row) => {
            const bidders = row.quotes || [];
            const remaining = remainingQtyOnItem(row, assignment[row.rfq_item_id]);
            const lowest = lowestByItem[row.rfq_item_id];
            return (
              <Box
                key={row.rfq_item_id}
                sx={{ border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}
              >
                <Box sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                  px: 1.5, py: 1, backgroundColor: IPD_COLORS.selectedBg,
                }}>
                  <Typography sx={{ fontWeight: 600 }}>{itemLabel(row)}</Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    Requested {formatQty(requestedQty(row))}
                    {remaining > 0
                      ? ` · remaining ${formatQty(remaining)}`
                      : ' · fully allocated'}
                  </Typography>
                </Box>
                {bidders.length === 0 ? (
                  <Typography sx={{ px: 1.5, py: 1.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
                    No vendor quoted this item.
                  </Typography>
                ) : (
                  <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Vendor who bid</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Quoted</TableCell>
                        <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 160 }}>Award qty</TableCell>
                        <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Line value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bidders.map((line) => {
                        const qtyValue = assignment[row.rfq_item_id]?.[line.vendor_quote_id] ?? 0;
                        const qty = parseAwardQty(qtyValue);
                        const cap = quotedQtyForLine(row, line);
                        const isLowest = lowest?.vendor_quote_id === line.vendor_quote_id;
                        const notLowest = qty > 0 && lowest && !isLowest;
                        return (
                          <TableRow key={line.vendor_quote_id}>
                            <TableCell>
                              <Typography sx={{ fontSize: 13, fontWeight: isLowest ? 600 : 400 }}>
                                {vendorMap[line.vendor_id] || line.vendor_id}
                                {isLowest ? ' (lowest)' : ''}
                              </Typography>
                              {notLowest && (
                                <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text }}>
                                  Not the lowest for this item.
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography sx={{ fontSize: 13 }}>
                                {formatQty(cap)} · {formatCurrency(line.line_total)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <InvTextField
                                  type="number"
                                  value={qtyValue === 0 ? '0' : (qtyValue ?? '')}
                                  onChange={(e) => setQty(row.rfq_item_id, line.vendor_quote_id, e.target.value)}
                                  inputProps={{ min: 0, max: cap, step: 'any' }}
                                  sx={{ width: 96 }}
                                />
                                <Button
                                  size="small"
                                  onClick={() => fillRemaining(row, line.vendor_quote_id, line)}
                                  sx={{ minWidth: 0, px: 0.75, fontSize: 11, color: IPD_COLORS.primary }}
                                >
                                  Max
                                </Button>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: qty > 0 ? 600 : 400 }}>
                              {qty > 0 ? formatCurrency(awardedLineValue(line, qty, row)) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
          {groups.map((g) => {
            const q = quoteById[g.quoteId];
            const qtyBits = g.lines.map((l) => formatQty(l.awarded_quantity)).join(' + ');
            return (
              <Chip
                key={g.quoteId}
                size="small"
                label={`${vendorMap[q?.vendor_id] || q?.vendor_id}: qty ${qtyBits} · ${formatCurrency(g.value)}`}
                sx={{ backgroundColor: IPD_COLORS.selectedBg, color: IPD_COLORS.textPrimary }}
              />
            );
          })}
        </Box>

        {totalValue > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Split award value {formatCurrency(totalValue)} across {groups.length} vendor(s) —
            approval route: <strong>{approvalRoute(totalValue).join(' → ')}</strong>
          </Alert>
        )}

        {blockers.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {blockers.map((b) => <Box component="li" key={b} sx={{ fontSize: 13 }}>{b}</Box>)}
            </Box>
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <InvTextField
            label="Award remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            multiline
            rows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {progress || (blockers.length ? `${blockers.length} to resolve first` : 'Ready to award')}
        </Typography>
        <Button onClick={onClose} disabled={loading} variant="outlined"
          sx={{ color: IPD_COLORS.textSecondary, borderColor: IPD_COLORS.border }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading || blockers.length > 0}
          variant="contained"
          sx={{ backgroundColor: IPD_COLORS.primary }}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
        >
          Award {formatCurrency(totalValue)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
